import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle, XCircle, Clock, DollarSign, Key, RefreshCw } from "lucide-react";

interface PaymentAudit {
  id: string;
  stripe_payment_intent_id: string;
  stripe_subscription_id: string;
  customer_email: string;
  amount: number;
  currency: string;
  plan_type: string;
  payment_date: string;
  created_at: string;
}

interface LicenseActivationAudit {
  id: string;
  license_key: string;
  customer_email: string;
  plan_type: string;
  stripe_subscription_id: string;
  activation_date: string;
  created_at: string;
}

interface RecoveryAudit {
  id: string;
  customer_email: string;
  stripe_subscription_id: string;
  recovery_status: string;
  license_created: boolean;
  error_message: string | null;
  recovery_date: string;
  created_at: string;
}

export default function AuditLogs() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"payments" | "licenses" | "recovery">("payments");
  const [payments, setPayments] = useState<PaymentAudit[]>([]);
  const [licenses, setLicenses] = useState<LicenseActivationAudit[]>([]);
  const [recoveries, setRecoveries] = useState<RecoveryAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState<any>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [gapAnalysis, setGapAnalysis] = useState<{
    totalPayments: number;
    withLicenses: number;
    missingLicenses: number;
  } | null>(null);

  useEffect(() => {
    if (!user || profile?.role !== "plugin_creator") {
      navigate("/");
      return;
    }

    loadAuditData();
  }, [user, profile, navigate]);

  const loadAuditData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, licensesRes, recoveriesRes] = await Promise.all([
        supabase
          .from("payment_audit")
          .select("*")
          .order("payment_date", { ascending: false })
          .limit(100),
        supabase
          .from("license_activation_audit")
          .select("*")
          .order("activation_date", { ascending: false })
          .limit(100),
        supabase
          .from("recovery_audit")
          .select("*")
          .order("recovery_date", { ascending: false })
          .limit(100),
      ]);

      if (paymentsRes.data) setPayments(paymentsRes.data);
      if (licensesRes.data) setLicenses(licensesRes.data);
      if (recoveriesRes.data) setRecoveries(recoveriesRes.data);

      const paymentSubs = new Set(paymentsRes.data?.map(p => p.stripe_subscription_id) || []);
      const licenseSubs = new Set(licensesRes.data?.map(l => l.stripe_subscription_id) || []);
      const missing = [...paymentSubs].filter(sub => !licenseSubs.has(sub)).length;

      setGapAnalysis({
        totalPayments: paymentSubs.size,
        withLicenses: licenseSubs.size,
        missingLicenses: missing,
      });
    } catch (error) {
      console.error("Error loading audit data:", error);
    } finally {
      setLoading(false);
    }
  };

  const runRecovery = async () => {
    setIsRecovering(true);
    setRecoveryResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/recover-missing-licenses`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      setRecoveryResult(result);

      await loadAuditData();
    } catch (error) {
      console.error("Error running recovery:", error);
      setRecoveryResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsRecovering(false);
    }
  };

  const importStripeHistory = async () => {
    setIsImporting(true);
    setImportResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/import-stripe-history`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            daysBack: 90,
            limit: 100,
          }),
        }
      );

      const result = await response.json();
      setImportResult(result);

      await loadAuditData();
    } catch (error) {
      console.error("Error importing history:", error);
      setImportResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading audit logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
          <p className="mt-2 text-gray-600">
            Track payments, license activations, and recovery attempts
          </p>
        </div>

        {gapAnalysis && gapAnalysis.missingLicenses > 0 && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="text-xl font-bold text-red-900 mb-2">
                  Action Required: {gapAnalysis.missingLicenses} Customer{gapAnalysis.missingLicenses !== 1 ? 's' : ''} Waiting for License Activation
                </h2>
                <p className="text-red-800 mb-4">
                  You have {gapAnalysis.totalPayments} payment{gapAnalysis.totalPayments !== 1 ? 's' : ''} but only {gapAnalysis.withLicenses} license{gapAnalysis.withLicenses !== 1 ? 's' : ''}.
                  {gapAnalysis.missingLicenses} paying customer{gapAnalysis.missingLicenses !== 1 ? 's are' : ' is'} waiting to be activated.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={runRecovery}
                    disabled={isRecovering}
                    className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
                  >
                    <RefreshCw className={`w-5 h-5 ${isRecovering ? "animate-spin" : ""}`} />
                    {isRecovering ? "Activating Customers..." : "Activate All Customers Now"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {gapAnalysis && gapAnalysis.missingLicenses === 0 && gapAnalysis.totalPayments > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold text-green-900">All Customers Activated</h3>
                <p className="text-green-700">
                  All {gapAnalysis.totalPayments} paying customer{gapAnalysis.totalPayments !== 1 ? 's have' : ' has'} active licenses.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Admin Tools</h2>
            <div className="flex gap-3">
              <button
                onClick={importStripeHistory}
                disabled={isImporting}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Clock className={`w-5 h-5 ${isImporting ? "animate-spin" : ""}`} />
                {isImporting ? "Importing..." : "Import Stripe History"}
              </button>
              <button
                onClick={runRecovery}
                disabled={isRecovering}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className={`w-5 h-5 ${isRecovering ? "animate-spin" : ""}`} />
                {isRecovering ? "Running Recovery..." : "Run Recovery"}
              </button>
            </div>
          </div>

          {importResult && (
            <div
              className={`p-4 rounded-lg mb-4 ${
                importResult.success ? "bg-blue-50 border border-blue-200" : "bg-red-50 border border-red-200"
              }`}
            >
              {importResult.success ? (
                <div>
                  <div className="flex items-center gap-2 text-blue-800 font-semibold mb-2">
                    <CheckCircle className="w-5 h-5" />
                    Import Completed Successfully
                  </div>
                  <div className="text-sm text-blue-700">
                    <p>Total Subscriptions Processed: {importResult.totalProcessed}</p>
                    <p>Imported: {importResult.summary.imported}</p>
                    <p>Already Exists: {importResult.summary.alreadyExists}</p>
                    <p>Failed: {importResult.summary.failed}</p>
                    {importResult.summary.imported > 0 && (
                      <p className="mt-2 font-semibold">Next: Click "Run Recovery" to activate these customers</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-800">
                  <XCircle className="w-5 h-5" />
                  <span>Import Failed: {importResult.error}</span>
                </div>
              )}
            </div>
          )}

          {recoveryResult && (
            <div
              className={`p-4 rounded-lg ${
                recoveryResult.success ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
              }`}
            >
              {recoveryResult.success ? (
                <div>
                  <div className="flex items-center gap-2 text-green-800 font-semibold mb-2">
                    <CheckCircle className="w-5 h-5" />
                    Recovery Completed Successfully
                  </div>
                  <div className="text-sm text-green-700">
                    <p>Total Payments Processed: {recoveryResult.totalPayments}</p>
                    <p>Recovered: {recoveryResult.summary.recovered}</p>
                    <p>Already Exists: {recoveryResult.summary.alreadyExists}</p>
                    <p>Failed: {recoveryResult.summary.failed}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-800">
                  <XCircle className="w-5 h-5" />
                  <span>Recovery Failed: {recoveryResult.error}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab("payments")}
                className={`flex-1 px-6 py-4 text-sm font-medium ${
                  activeTab === "payments"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Payments ({payments.length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab("licenses")}
                className={`flex-1 px-6 py-4 text-sm font-medium ${
                  activeTab === "licenses"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Key className="w-5 h-5" />
                  License Activations ({licenses.length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab("recovery")}
                className={`flex-1 px-6 py-4 text-sm font-medium ${
                  activeTab === "recovery"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <RefreshCw className="w-5 h-5" />
                  Recovery Attempts ({recoveries.length})
                </div>
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === "payments" && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subscription ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.customer_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {payment.plan_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatAmount(payment.amount, payment.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {payment.stripe_subscription_id?.substring(0, 20)}...
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(payment.payment_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "licenses" && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        License Key
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subscription ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Activation Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {licenses.map((license) => (
                      <tr key={license.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {license.customer_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {license.license_key}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            {license.plan_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {license.stripe_subscription_id?.substring(0, 20)}...
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(license.activation_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "recovery" && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        License Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Error
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recoveries.map((recovery) => (
                      <tr key={recovery.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {recovery.customer_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              recovery.recovery_status === "activated"
                                ? "bg-green-100 text-green-800"
                                : recovery.recovery_status === "failed"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {recovery.recovery_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {recovery.license_created ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {recovery.error_message || "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(recovery.recovery_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
